/* eslint-disable indent */
import React from 'react';
import PropTypes from 'prop-types';
import { Bert } from 'meteor/themeteorchef:bert';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  IconButton,
  Grid,
  Modal,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { isEmpty } from 'lodash';
import uuid from 'uuid/v1';
import DeleteIcon from '@mui/icons-material/Delete';
import handleErrorInClient from '../../../modules/handle-error-in-client';

const MapsConfigSharedModal = props => {
  const {
    atlas,
    handleModalUserShared,
    isAtlas,
    map,
    openModalSharedConfig,
    sharedConfiguration
  } = props;
  const generateRandomId = length => {
    return Array.from({ length }, () => Math.random().toString(36)[2]).join('');
  };
  const initialEmpty =
    isAtlas === true
      ? { userList: [], isNameShared: false }
      : { userList: [], deleteAnnotations: false, isNameShared: false };
  const sharedConfigurationInitial =
    sharedConfiguration && !isEmpty(sharedConfiguration)
      ? {
          ...sharedConfiguration,
          userList: sharedConfiguration.userList.map(user => ({
            ...user,
            id: user.id || generateRandomId(18),
            secondVerification: user.secondVerification || false
          }))
        }
      : initialEmpty;

  const fileInputRef = React.useRef(null);
  const itemsPerPage = 5;
  const [user, setUser] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [password, setPassword] = React.useState('');
  const [localSettings, setLocalSettings] = React.useState(sharedConfigurationInitial);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const [step, setStep] = React.useState(1);

  const handleClose = () => {
    handleModalUserShared(localSettings);
  };

  const handleAddUser = () => {
    const newUser = { user, password, secondVerification: false, id: generateRandomId(18) };
    setLocalSettings(prev => ({
      ...prev,
      userList: [...prev.userList, newUser]
    }));
    setUser('');
    setPassword('');
  };

  const handleDeleteUser = index => {
    setLocalSettings(prev => ({
      ...prev,
      userList: prev.userList.filter((_, i) => i !== index)
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleDownload = () => {
    const download = async () => {
      try {
        const filename =
          isAtlas === true ? `Usuarios-${atlas.name}.json` : `Usuarios-${map.name}.json`;
        const json = JSON.stringify(localSettings.userList, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const enlaceDescarga = document.createElement('a');
        enlaceDescarga.href = url;
        enlaceDescarga.download = filename;
        enlaceDescarga.click();
        URL.revokeObjectURL(url);
        Bert.alert('Layer download!', 'info');
      } catch (error) {
        handleErrorInClient(error);
      }
    };
    download();
  };

  const combinarArraysSinDuplicados = (array1, array2) => {
    const arrayFiltrado = array2.filter(
      objeto2 =>
        !array1.some(
          objeto1 => objeto1.user === objeto2.user && objeto1.password === objeto2.password
        )
    );
    return array1.concat(arrayFiltrado);
  };

  const loadFile = file => {
    const fileReader = new FileReader();
    fileReader.readAsText(file);
    fileReader.onload = async () => {
      const { result } = fileReader;
      const parsedText = JSON.parse(result);
      const arrayCombinado = combinarArraysSinDuplicados(
        localSettings.userList,
        parsedText.map(element => ({
          ...element,
          id: element.id || generateRandomId(18),
          secondVerification: element.secondVerification || false
        }))
      );
      setLocalSettings({
        ...localSettings,
        userList: arrayCombinado
      });
    };

    fileReader.onerror = () => {
      Bert.alert('Error on load file', 'danger');
    };
  };

  return (
    <>
      <Modal
        open={openModalSharedConfig}
        style={{
          bgcolor: 'white',
          top: '2.5vh',
          left: '2.5vw'
        }}
      >
        <Box backgroundColor="white" width="95vw" height="95vh">
          <Box display="flex" height="10%" width="90%" alignItems="center" mr={5}>
            <Grid container direction="row" justifyContent="space-around" spacing={2}>
              <Grid item xs={1} />
              <Grid item xs={5} sx={{ display: 'flex' }}>
                <ButtonGroup variant="outlined" aria-label="Basic button group">
                  <Button
                    onClick={() => setStep(1)}
                    sx={{
                      color: '#2A3F90',
                      backgroundColor: 'white',
                      borderColor: '#2A3F90'
                    }}
                  >
                    Users to enter the shared map
                  </Button>
                  <Button
                    onClick={() => setStep(2)}
                    sx={{
                      color: '#2A3F90',
                      backgroundColor: 'white',
                      borderColor: '#2A3F90'
                    }}
                  >
                    Configuration
                  </Button>
                </ButtonGroup>
              </Grid>
              <Grid item xs={3} />
              <Grid item xs={3} sx={{ justifyContent: 'space-around', display: 'flex' }}>
                {step === 1 && (
                  <>
                    <Button
                      variant="outlined"
                      sx={{
                        color: '#2A3F90',
                        backgroundColor: 'white',
                        borderColor: '#2A3F90'
                      }}
                      onClick={() => {
                        fileInputRef.current.click();
                      }}
                      size="small"
                    >
                      Importar
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      style={{ opacity: 0, position: 'absolute', zIndex: -1 }}
                      onChange={event => {
                        loadFile(event.target.files[0]);
                      }}
                    />
                    <Button
                      variant="outlined"
                      sx={{
                        color: '#2A3F90',
                        backgroundColor: 'white',
                        borderColor: '#2A3F90'
                      }}
                      onClick={handleDownload}
                      size="small"
                    >
                      Exportar
                    </Button>
                  </>
                )}
                <Button color="success" variant="outlined" onClick={handleClose} size="small">
                  Close
                </Button>
              </Grid>
            </Grid>
          </Box>
          {step === 1 && (
            <Box height="85%" width="90%" m={3}>
              <Box mb={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <Typography variant="subtitle1">User</Typography>
                    <TextField
                      id="outlined-required"
                      label="Introduce un usuario"
                      size="small"
                      value={user}
                      onChange={event => setUser(event.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle1">Password</Typography>
                    <TextField
                      id="outlined-required"
                      label="Introduce una contraseña"
                      size="small"
                      value={password}
                      onChange={event => setPassword(event.target.value)}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Button variant="contained" color="success" onClick={handleAddUser}>
                  Add
                </Button>
              </Box>
              <Table>
                <TableBody>
                  {localSettings.userList.slice(startIndex, endIndex).map((item, index) => (
                    <TableRow key={uuid()}>
                      <TableCell>{item.user}</TableCell>
                      <TableCell>{item.password}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteUser(startIndex + index)}
                          sx={{
                            color: '#2A3F90'
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                        {/* <Checkbox
                          checked={item.secondVerification || false}
                          onChange={() => {
                            const updatedUserList = localSettings.userList.map((element, i) => {
                              if (i === index) {
                                return {
                                  ...element,
                                  secondVerification: !element.secondVerification
                                };
                              }
                              return element;
                            });
                            setLocalSettings({
                              ...localSettings,
                              userList: updatedUserList
                            });
                          }}
                          sx={{
                            color: '#2A3F90',
                            '&.Mui-checked': {
                              color: '#2A3F90'
                            }
                          }}
                          value="Verificación"
                        /> */}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Stack
                direction="row"
                spacing={2}
                sx={{
                  marginTop: '20px',
                  justifyContent: 'center'
                }}
              >
                <Pagination
                  count={Math.ceil(localSettings.userList.length / itemsPerPage)}
                  page={page}
                  onChange={handleChangePage}
                  sx={{
                    '& .MuiPaginationItem-page.Mui-selected': {
                      backgroundColor: '#2A3F90',
                      color: 'white'
                    },
                    '& .MuiPaginationItem-page.Mui-selected .MuiPaginationItem-pageNumber': {
                      color: 'white'
                    }
                  }}
                />
              </Stack>
            </Box>
          )}
          {step === 2 && (
            <Box height="85%" width="90%" m={3}>
              {isAtlas !== true && (
                <Box display="row" justifyContent="space-evenly" fullwidth="true" mb={2}>
                  <Checkbox
                    checked={localSettings.deleteAnnotations}
                    onChange={() => {
                      setLocalSettings({
                        ...localSettings,
                        deleteAnnotations: !localSettings.deleteAnnotations
                      });
                    }}
                    sx={{
                      color: '#2A3F90',
                      '&.Mui-checked': {
                        color: '#2A3F90'
                      }
                    }}
                    value="deleteAnnotations"
                  />
                  Delete annotations
                </Box>
              )}
              <Box display="row" justifyContent="space-evenly" fullwidth="true" mb={2}>
                <Checkbox
                  checked={localSettings.isNameShared}
                  onChange={() => {
                    setLocalSettings({
                      ...localSettings,
                      isNameShared: !localSettings.isNameShared
                    });
                  }}
                  sx={{
                    color: '#2A3F90',
                    '&.Mui-checked': {
                      color: '#2A3F90'
                    }
                  }}
                  value="isNameShared"
                />
                Display the name field for the user to fill in.
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default MapsConfigSharedModal;

MapsConfigSharedModal.defaultProps = {
  atlas: {},
  openModalSharedConfig: false,
  map: {},
  isAtlas: false
};

MapsConfigSharedModal.propTypes = {
  atlas: PropTypes.object,
  handleModalUserShared: PropTypes.func.isRequired,
  map: PropTypes.object,
  openModalSharedConfig: PropTypes.bool,
  sharedConfiguration: PropTypes.object.isRequired,
  isAtlas: PropTypes.bool
};
